import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ModalLayout } from '../../components/Modal/ModalLayout';
import { FormField } from '../../components/Form/FormField';
import { useApiKeys } from '../../hooks/useApiKeys';
import { ApiKey, Role } from '../../types/apiKey';
import { Switch } from '../../components/Form/Switch';

interface EditApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  apiKey: ApiKey;
}

const AVAILABLE_ROLES: Role[] = ['REPORT', 'SYSTEM'];

export const EditApiKeyModal = ({ isOpen, onClose, apiKey }: EditApiKeyModalProps) => {
  const { t } = useTranslation();
  const { updateApiKey } = useApiKeys();

  const formik = useFormik({
    initialValues: {
      name: apiKey.name,
      description: apiKey.description || '',
      roles: apiKey.roles,
      status: apiKey.status === 'ACTIVE',
    },
    validationSchema: Yup.object({
      name: Yup.string().required(t('validation.required')),
      description: Yup.string(),
      roles: Yup.array()
        .of(Yup.string().oneOf(AVAILABLE_ROLES))
        .min(1, t('validation.minItems', { min: 1 }))
        .required(t('validation.required')),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await updateApiKey.mutateAsync({
          key: apiKey.key,
          data: {
            name: values.name,
            description: values.description,
            roles: values.roles,
            status: values.status ? 'ACTIVE' : 'DISABLED',
          },
        });
        onClose();
      } catch (error) {
        console.error('Failed to update API key:', error);
        setSubmitting(false);
      }
    },
  });

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      title={t('apiKeys.modal.editKey')}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="space-y-4">
          <FormField
            label={t('apiKeys.modal.name')}
            name="name"
            formik={formik}
          />

          <FormField
            label={t('apiKeys.modal.description')}
            name="description"
            type="textarea"
            formik={formik}
          />

          <div>
            <label className="block text-sm font-medium mb-2">
              {t('apiKeys.modal.roles')}
            </label>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_ROLES.map((role) => (
                <label key={role} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={formik.values.roles.includes(role)}
                    onChange={() => {
                      const newRoles = formik.values.roles.includes(role)
                        ? formik.values.roles.filter((r) => r !== role)
                        : [...formik.values.roles, role];
                      formik.setFieldValue('roles', newRoles);
                    }}
                  />
                  <span className="text-sm">{role}</span>
                </label>
              ))}
            </div>
            {formik.touched.roles && formik.errors.roles && (
              <div className="text-error text-sm mt-1">{formik.errors.roles}</div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">{t('apiKeys.modal.status')}</span>
            <Switch
              checked={formik.values.status}
              onChange={() => formik.setFieldValue('status', !formik.values.status)}
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={formik.isSubmitting}
          >
            {t('apiKeys.modal.save')}
          </button>
        </div>
      </form>
    </ModalLayout>
  );
}; 